import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PiCalendarDuotone,
  PiCheckCircleDuotone,
  PiMagnifyingGlassDuotone,
  PiSwordDuotone,
  PiWarningDuotone,
} from 'react-icons/pi';
import { searchExternalEvents } from '../api/eventService';
import { accessAwareMessage } from '../api/api';
import Badge from '../shared/common/Badge';
import Breadcrumb from '../shared/common/Breadcrumb';
import Button from '../shared/common/Button';
import Card from '../shared/common/Card';
import EmptyState from '../shared/common/EmptyState';
import Input from '../shared/common/Input';
import { SkeletonGrid } from '../shared/common/Skeleton';
import { buildRoomSelectionPathForExternalEvent } from '../shared/utils/challengeCreationFlow';
import { buildCreateEventCopyPathForExternalEvent } from '../shared/utils/externalEventCopyFlow';

const DEFAULT_COMPETITION = 'PL';
const FOOTBALL_DATA_PROVIDER = 'football-data.org';
const API_NOT_CONFIGURED_HINT = 'Если список пустой, а в логах backend есть «External sports API is not configured», проверь переменные EXTERNAL_SPORTS_API_PROVIDER, EXTERNAL_SPORTS_API_BASE_URL, EXTERNAL_SPORTS_API_KEY и EXTERNAL_SPORTS_API_KEY_HEADER.';
const POPULAR_COMPETITIONS = [
  { code: 'PL', name: 'Англия' },
  { code: 'CL', name: 'Еврокубки' },
  { code: 'BL1', name: 'Германия' },
  { code: 'SA', name: 'Италия' },
  { code: 'PD', name: 'Испания' },
];
const EXTERNAL_EVENT_STATE_KEY = 'externalEvent';

const normalizeCompetitionCode = (value) => String(value || '').trim().toUpperCase();

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  // Ensure the date string is interpreted correctly:
  // - ISO strings with offset (e.g. "2025-01-15T15:00:00Z" or "...+03:00") are parsed as absolute moments
  // - ISO strings without offset (e.g. "2025-01-15T15:00:00") are treated as UTC by appending 'Z',
  //   since external API dates (football-data.org utcDate) are always in UTC
  const normalizedDateStr = typeof dateStr === 'string' && isUtcCandidate(dateStr) ? dateStr + 'Z' : dateStr;
  return new Date(normalizedDateStr).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Detects ISO date-time strings without timezone offset (e.g. "2025-01-15T15:00:00").
 * These are treated as UTC when they come from external APIs.
 */
const isUtcCandidate = (dateStr) => {
  // Match YYYY-MM-DDTHH:MM(:SS) without trailing Z or ±HH:MM offset
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(dateStr);
};

const formatEventDate = (event) => {
  const start = formatDate(event?.startDate || event?.eventDate || event?.date);
  const end = formatDate(event?.endDate);
  if (start && end) return `${start} — ${end}`;
  return start || end || 'Дата не указана';
};

const isFutureEvent = (event) => {
  const startDate = event?.startDate || event?.eventDate || event?.date;
  if (!startDate) return false;
  return new Date(startDate).getTime() > Date.now();
};

export default function ExternalEventsPage() {
  const navigate = useNavigate();
  const [competition, setCompetition] = useState(DEFAULT_COMPETITION);
  const [events, setEvents] = useState([]);
  const [searchedCompetition, setSearchedCompetition] = useState('');
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedExternalId, setCopiedExternalId] = useState('');

  const futureEvents = useMemo(
    () => events.filter((event) => isFutureEvent(event)),
    [events],
  );

  const pastEvents = useMemo(
    () => events.filter((event) => !isFutureEvent(event)),
    [events],
  );

  const visibleEvents = [...futureEvents, ...pastEvents];

  useEffect(() => {
    document.title = 'BetMate — Футбольный API';
  }, []);

  const handleSearch = () => searchCompetition(competition);

  const searchCompetition = async (competitionCode) => {
    const normalizedCompetition = normalizeCompetitionCode(competitionCode);
    if (!normalizedCompetition) {
      setError('Укажи код турнира, например PL или CL');
      return;
    }

    setCompetition(normalizedCompetition);
    setSearchedCompetition(normalizedCompetition);
    setSearched(true);
    setError('');
    setCopiedExternalId('');
    setLoading(true);
    try {
      const data = await searchExternalEvents(normalizedCompetition);
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setEvents([]);
      setError(getSearchError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCompetitionKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleSearch();
  };

  const handleCompetitionPreset = (code) => {
    searchCompetition(code);
  };

  const handleCopyExternalId = async (externalId) => {
    if (!externalId) return;
    try {
      await navigator.clipboard.writeText(externalId);
      setCopiedExternalId(externalId);
    } catch (err) {
      setError('Не удалось скопировать externalId. Скопируй его из карточки вручную.');
    }
  };

  const handleCreateChallengeFromExternalEvent = (event) => {
    if (!event?.externalId || !isFutureEvent(event)) return;
    navigate(buildRoomSelectionPathForExternalEvent(
      event.externalId,
      searchedCompetition || competition,
      event.competitionName || '',
    ));
  };

  const handleCopyExternalEventAsManual = (event) => {
    if (!event?.externalId) return;
    navigate(
      buildCreateEventCopyPathForExternalEvent(event.externalId, searchedCompetition || competition),
      { state: { [EXTERNAL_EVENT_STATE_KEY]: event } },
    );
  };

  const getSearchError = (err) => (
    err?.response?.data?.message
    || accessAwareMessage(err, 'Не удалось загрузить внешние футбольные события. Проверь код турнира и настройки backend.')
  );

  return (
    <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)' }}>
      <Breadcrumb
        items={[
          { label: 'События', to: '/events' },
          { label: 'Футбольный API' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="bm-h2">Футбольные события</h1>
          <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>
Поиск матчей из {FOOTBALL_DATA_PROVIDER}: можно создать официальное пари или скопировать матч как своё событие
          </p>
        </div>
      </div>

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) auto', gap: 10, alignItems: 'end' }}>
          <Input
            label="Код турнира"
            value={competition}
            onChange={(e) => setCompetition(e.target.value.toUpperCase())}
            onKeyDown={handleCompetitionKeyDown}
            placeholder="PL, CL, BL1..."
            error={error && !searched ? error : ''}
          />
          <Button
            variant="primary"
            loading={loading}
            icon={<PiMagnifyingGlassDuotone size={16} />}
            onClick={handleSearch}
            style={{ marginBottom: error && !searched ? 22 : 0 }}
          >
            Найти матчи
          </Button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {POPULAR_COMPETITIONS.map((item) => (
            <Button
              key={item.code}
              variant="secondary"
              size="sm"
              onClick={() => handleCompetitionPreset(item.code)}
            >
              {item.code} · {item.name}
            </Button>
          ))}
        </div>

        <p className="bm-text-secondary bm-text-sm">
Официальное пари будет привязано к externalId и синхронизации результата. Копия создаст отдельное ручное событие,
          где можно изменить даты, описание и собственные исходы перед сохранением.
        </p>
      </Card>

      {error && searched && <div className="bm-auth-error">{error}</div>}

      {loading ? (
        <SkeletonGrid count={6} />
      ) : !searched ? (
        <EmptyState
          icon={<PiMagnifyingGlassDuotone size={40} />}
          title="Найди футбольные события"
          hint="Например, введи PL и нажми «Найти матчи»"
        />
      ) : visibleEvents.length === 0 ? (
        <EmptyState
          icon={<PiWarningDuotone size={40} />}
          title={`Матчи ${searchedCompetition || competition} не найдены`}
          hint={API_NOT_CONFIGURED_HINT}
          actionLabel="Попробовать CL"
          onAction={() => handleCompetitionPreset('CL')}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <h2 className="bm-h3">Найдено матчей: {visibleEvents.length}</h2>
            <p className="bm-text-secondary bm-text-sm">
              Будущие матчи показаны первыми
            </p>
          </div>

          <div className="bm-grid">
            {visibleEvents.map((event) => {
              const future = isFutureEvent(event);
              const copied = copiedExternalId === event.externalId;
              const outcomes = Array.isArray(event.outcomes) ? event.outcomes : [];

              return (
                <Card key={event.externalId} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 'var(--bm-radius-sm)',
                        background: future ? 'rgba(0,214,143,0.1)' : 'rgba(255,255,255,0.06)',
                        color: future ? 'var(--bm-accent-green)' : 'var(--bm-text-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <PiCalendarDuotone size={20} />
                    </div>
                    <Badge status={future ? 'open' : 'closed'} pulse={future}>
{future ? 'будущий матч' : 'не для нового официального пари'}
                    </Badge>
                    {event.category && <Badge status="primary">{event.category}</Badge>}
                    {event.competitionName && <Badge status="primary">{event.competitionName}</Badge>}
                  </div>

                  <div>
                    <h4 className="bm-h4">{event.title || event.externalId}</h4>
                    {event.description && (
                      <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>
                        {event.description}
                      </p>
                    )}
                  </div>

                  <p className="bm-text-secondary bm-text-sm">
                    Период: {formatEventDate(event)}
                  </p>
                  <p className="bm-text-secondary bm-text-sm">
                    externalId: <span className="bm-font-medium">{event.externalId}</span>
                  </p>
                  <p className="bm-text-secondary bm-text-sm">
Официальное пари использует внешний результат. Копия станет твоим ручным событием без автосинхронизации.
                  </p>

                  {outcomes.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {outcomes.map((outcome) => (
                        <Badge key={outcome} status="primary">{outcome}</Badge>
                      ))}
                    </div>
                  )}

                  {event.officialResult && (
                    <p className="bm-text-secondary bm-text-sm">
                      Официальный результат: {event.officialResult}
                    </p>
                  )}

                  <div style={{ marginTop: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button
                      variant={copied ? 'success' : 'secondary'}
                      size="sm"
                      icon={copied ? <PiCheckCircleDuotone size={14} /> : undefined}
                      onClick={() => handleCopyExternalId(event.externalId)}
                    >
                      {copied ? 'externalId скопирован' : 'Скопировать externalId'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleCopyExternalEventAsManual(event)}
                    >
                      Скопировать как своё событие
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<PiSwordDuotone size={14} />}
                      disabled={!future}
                      onClick={() => handleCreateChallengeFromExternalEvent(event)}
                    >
{future ? 'Официальное пари' : 'Матч уже начался'}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
